import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, api, TransportError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button, TextField } from '../ui/index.js';

type ThreadList = Awaited<ReturnType<typeof api.get<'/api/v1/mail/threads'>>>;
type Thread = ThreadList['threads'][number];
type ThreadDetail = Awaited<ReturnType<typeof api.get<'/api/v1/mail/threads/{threadId}'>>>;
type MessageBody = Awaited<ReturnType<typeof api.get<'/api/v1/mail/messages/{messageId}/body'>>>;
type MailboxList = Awaited<ReturnType<typeof api.get<'/api/v1/mailboxes'>>>;

/**
 * Reading mail.
 *
 * The screen that lets somebody stop switching to Outlook: read, reply, compose, download an
 * attachment. Everything else that changes a mailbox goes through the action path where policy,
 * the journal and undo live.
 *
 * **A body is fetched when it is opened, not before.** Sync stores envelopes and a snippet; the
 * mail itself stays with the provider until somebody asks for it. Opening a message therefore
 * costs a round trip the first time and nothing afterwards — and a list of forty threads costs
 * no mail traffic at all.
 *
 * **The body is rendered into a sandboxed frame, never into this document.** The server sanitises
 * before it sends, and the frame is the second wall: a sanitiser bug becomes a rendering
 * curiosity rather than a sender's JavaScript running in a reader's session with their cookies.
 */
export function MailScreen(): ReactNode {
  const { t, translator } = useI18n();
  const { activeWorkspace } = useAuth();

  const [threads, setThreads] = useState<ThreadList | null>(null);
  const [mailboxes, setMailboxes] = useState<MailboxList['mailboxes'] | null>(null);
  const [scopeMailboxId, setScopeMailboxId] = useState<string | null>(null);
  const [openThread, setOpenThread] = useState<ThreadDetail | null>(null);
  const [bodies, setBodies] = useState<Record<string, MessageBody>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sentNote, setSentNote] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [compose, setCompose] = useState({ mailboxId: '', to: '', cc: '', subject: '', text: '' });
  const [composeNote, setComposeNote] = useState<string | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [draftNote, setDraftNote] = useState<string | null>(null);
  const [tone, setTone] = useState<'formal' | 'neutral' | 'friendly' | 'brief' | 'warm'>('neutral');

  const tenantId = activeWorkspace?.id;

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (tenantId === undefined) return;
      try {
        const result = await api.get(
          '/api/v1/mail/threads',
          scopeMailboxId === null ? undefined : { mailboxId: scopeMailboxId },
          { tenantId, signal },
        );
        setThreads(result);
        setFailure(null);
      } catch (error) {
        if (signal?.aborted === true) return;
        setThreads(null);
        setFailure(
          error instanceof ApiError
            ? error.code
            : error instanceof TransportError
              ? 'UNREACHABLE'
              : 'INTERNAL_ERROR',
        );
      }
    },
    [tenantId, scopeMailboxId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (tenantId === undefined) return;
    const controller = new AbortController();
    void api
      .get('/api/v1/mailboxes', undefined, { tenantId, signal: controller.signal })
      .then((result) => setMailboxes(result.mailboxes))
      .catch(() => setMailboxes(null));
    return () => controller.abort();
  }, [tenantId]);

  const openConversation = useCallback(
    async (thread: Thread) => {
      if (tenantId === undefined) return;
      setBusy(true);
      try {
        const detail = await api.get(
          '/api/v1/mail/threads/{threadId}',
          { threadId: thread.threadId },
          { tenantId },
        );
        setOpenThread(detail);

        /* The newest message is the one somebody opened the conversation to read, so its body is
           fetched immediately and the rest wait to be asked for. */
        const newest = detail.messages.at(-1);
        if (newest !== undefined && bodies[newest.messageId] === undefined) {
          const body = await api.get(
            '/api/v1/mail/messages/{messageId}/body',
            { messageId: newest.messageId },
            { tenantId },
          );
          setBodies((current) => ({ ...current, [newest.messageId]: body }));
        }
      } catch (error) {
        setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
      } finally {
        setBusy(false);
      }
    },
    [tenantId, bodies],
  );

  const openBody = useCallback(
    async (messageId: string) => {
      if (tenantId === undefined || bodies[messageId] !== undefined) return;
      setBusy(true);
      try {
        const body = await api.get(
          '/api/v1/mail/messages/{messageId}/body',
          { messageId },
          { tenantId },
        );
        setBodies((current) => ({ ...current, [messageId]: body }));
      } catch (error) {
        setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
      } finally {
        setBusy(false);
      }
    },
    [tenantId, bodies],
  );

  /*
   * A download is a fetch, not a link. The tenant travels in a header the way every other request
   * sends it, and a bare anchor cannot carry headers — so the bytes arrive as a blob and leave as
   * a click on an object URL.
   */
  const downloadAttachment = useCallback(
    async (messageId: string, index: number, filename: string) => {
      if (tenantId === undefined) return;
      setBusy(true);
      try {
        const response = await fetch(`/api/v1/mail/messages/${messageId}/attachments/${index}`, {
          credentials: 'same-origin',
          headers: { 'x-tenant-id': tenantId },
        });
        if (!response.ok) throw new Error(`download failed: ${response.status}`);
        const url = URL.createObjectURL(await response.blob());
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename === '' ? `attachment-${index}` : filename;
        anchor.click();
        URL.revokeObjectURL(url);
      } catch {
        setFailure('UNREACHABLE');
      } finally {
        setBusy(false);
      }
    },
    [tenantId],
  );

  const sendCompose = useCallback(async () => {
    if (tenantId === undefined) return;
    setBusy(true);
    setComposeNote(null);
    const addresses = (value: string): string[] =>
      value
        .split(/[,;\s]+/u)
        .map((address) => address.trim())
        .filter((address) => address !== '');
    try {
      await api.post(
        '/api/v1/mail/send',
        {
          mailboxId: compose.mailboxId,
          to: addresses(compose.to),
          cc: addresses(compose.cc),
          subject: compose.subject,
          text: compose.text,
        },
        undefined,
        { tenantId },
      );
      setCompose({ mailboxId: compose.mailboxId, to: '', cc: '', subject: '', text: '' });
      setComposing(false);
      setComposeNote(t('mail.compose.sent'));
    } catch {
      setComposeNote(t('mail.compose.failed'));
    } finally {
      setBusy(false);
    }
  }, [tenantId, compose, t]);

  /*
   * A suggestion, never a sending. What comes back lands in the same box the person types in,
   * so the only way it reaches anybody is if they read it and press send themselves.
   */
  const suggestReply = useCallback(async () => {
    const newest = openThread?.messages.at(-1);
    if (tenantId === undefined || newest === undefined) return;
    setDrafting(true);
    setDraftNote(null);
    try {
      const draft = await api.post(
        '/api/v1/mail/messages/{messageId}/draft-reply',
        { tone },
        { messageId: newest.messageId },
        { tenantId },
      );
      setReplyText(draft.body);
      setDraftNote(
        draft.warnings.length === 0
          ? t('mail.draft.ready')
          : t('mail.draft.readyWithWarnings', {
              warnings: draft.warnings.map((w) => t(`mail.draft.warning.${w}`)).join(', '),
            }),
      );
    } catch {
      setDraftNote(t('mail.draft.failed'));
    } finally {
      setDrafting(false);
    }
  }, [tenantId, openThread, tone, t]);

  const sendReply = useCallback(
    async (replyAll: boolean) => {
      const newest = openThread?.messages.at(-1);
      if (tenantId === undefined || newest === undefined) return;
      setBusy(true);
      setSentNote(null);
      try {
        await api.post(
          '/api/v1/mail/messages/{messageId}/reply',
          { text: replyText, replyAll },
          { messageId: newest.messageId },
          { tenantId },
        );
        setReplyText('');
        setSentNote(t('mail.reply.sent'));
      } catch (error) {
        setSentNote(
          error instanceof ApiError && error.code === 'INTERNAL_ERROR'
            ? t('mail.reply.failed')
            : t('mail.reply.failed'),
        );
      } finally {
        setBusy(false);
      }
    },
    [tenantId, openThread, replyText, t],
  );

  if (failure !== null && threads === null) {
    return (
      <Alert tone="critical" title={t('mail.error.title')}>
        <Button onClick={() => void load()}>{t('today.retry')}</Button>
      </Alert>
    );
  }

  if (threads === null) {
    return (
      <div className="boot" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    );
  }

  if (openThread !== null) {
    return (
      <>
        <header className="greeting">
          <Button onClick={() => setOpenThread(null)}>{t('mail.backToList')}</Button>
          <h1 className="greeting__title">{openThread.messages[0]?.subject ?? ''}</h1>
        </header>

        {openThread.messages.map((message) => {
          const body = bodies[message.messageId];
          return (
            <section className="card mail-message" key={message.messageId}>
              <div className="mail-message__head">
                <span className="mail-message__from">{message.fromName ?? message.from}</span>
                <span className="today-list__meta">
                  {translator.formatDate(new Date(message.receivedAt))}
                </span>
              </div>
              {body === undefined ? (
                <div className="mail-message__closed">
                  <p className="today-list__meta">{message.snippet}</p>
                  <Button disabled={busy} onClick={() => void openBody(message.messageId)}>
                    {t('mail.openMessage')}
                  </Button>
                </div>
              ) : (
                <>
                  {body.blockedRemoteResources > 0 && (
                    /* Said, not silently done: a reader learns the message wanted to phone home. */
                    <p className="mail-message__blocked">
                      {t('mail.remoteBlocked', { count: body.blockedRemoteResources })}
                    </p>
                  )}
                  {/*
                    A sandboxed frame with no allow-* flags: no scripts, no forms, no navigation,
                    and a unique opaque origin, so the message cannot reach this document even if
                    the sanitiser missed something.
                  */}
                  <iframe
                    className="mail-message__body"
                    title={message.subject}
                    sandbox=""
                    srcDoc={body.html}
                  />
                  {body.attachments.length > 0 && (
                    <ul className="today-list">
                      {body.attachments.map((attachment, index) => (
                        <li className="today-list__item" key={`${index}-${attachment.filename}`}>
                          <button
                            type="button"
                            className="link-button"
                            disabled={busy}
                            onClick={() =>
                              void downloadAttachment(message.messageId, index, attachment.filename)
                            }
                          >
                            {attachment.filename === ''
                              ? t('mail.attachment.unnamed')
                              : attachment.filename}
                          </button>
                          <span className="today-list__meta">
                            {translator.formatNumber(Math.round(attachment.sizeBytes / 1024))} kB
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </section>
          );
        })}

        {/*
          A reply is a person's own words, sent as written. Butler's drafting produces drafts;
          this box produces mail — which is why it is exactly a textarea and two buttons.
        */}
        <section className="card">
          <h2 className="card__title">{t('mail.reply.title')}</h2>
          {sentNote !== null && <p className="mail-message__blocked">{sentNote}</p>}
          <textarea
            className="mail-compose__text"
            rows={6}
            value={replyText}
            onChange={(event) => setReplyText(event.currentTarget.value)}
            placeholder={t('mail.reply.placeholder')}
          />
          {draftNote !== null && (
            <p className="mail-message__blocked" role="status">
              {draftNote}
            </p>
          )}
          <div className="mailbox-form__actions">
            <label className="field-group__label" htmlFor="draft-tone">
              {t('mail.draft.tone')}
            </label>
            <select
              id="draft-tone"
              className="select"
              value={tone}
              onChange={(event) =>
                setTone(
                  event.currentTarget.value as 'formal' | 'neutral' | 'friendly' | 'brief' | 'warm',
                )
              }
            >
              {(['neutral', 'friendly', 'formal', 'brief', 'warm'] as const).map((option) => (
                <option key={option} value={option}>
                  {t(`mail.draft.tone.${option}`)}
                </option>
              ))}
            </select>
            <Button disabled={busy || drafting} onClick={() => void suggestReply()}>
              {drafting ? t('mail.draft.working') : t('mail.draft.suggest')}
            </Button>
          </div>
          <div className="mailbox-form__actions">
            <Button
              variant="primary"
              disabled={busy || replyText.trim() === ''}
              onClick={() => void sendReply(false)}
            >
              {t('mail.reply.send')}
            </Button>
            <Button disabled={busy || replyText.trim() === ''} onClick={() => void sendReply(true)}>
              {t('mail.reply.sendAll')}
            </Button>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t('mail.title')}</h1>
        <Button
          variant="primary"
          onClick={() => {
            setComposeNote(null);
            setCompose((current) => ({
              ...current,
              mailboxId: current.mailboxId === '' ? (mailboxes?.[0]?.id ?? '') : current.mailboxId,
            }));
            setComposing((current) => !current);
          }}
        >
          {t('mail.compose.title')}
        </Button>
      </header>

      {composeNote !== null && !composing && <p className="mail-message__blocked">{composeNote}</p>}

      {composing && (
        <section className="card">
          <h2 className="card__title">{t('mail.compose.title')}</h2>
          {composeNote !== null && <p className="mail-message__blocked">{composeNote}</p>}
          {mailboxes !== null && mailboxes.length > 1 && (
            <div className="field-group">
              <label className="field-group__label" htmlFor="compose-from">
                {t('mail.compose.from')}
              </label>
              <select
                id="compose-from"
                className="select"
                value={compose.mailboxId}
                onChange={(event) =>
                  setCompose((current) => ({ ...current, mailboxId: event.currentTarget.value }))
                }
              >
                {mailboxes.map((mailbox) => (
                  <option key={mailbox.id} value={mailbox.id}>
                    {mailbox.emailAddress}
                  </option>
                ))}
              </select>
            </div>
          )}
          <TextField
            label={t('mail.compose.to')}
            value={compose.to}
            onChange={(event) =>
              setCompose((current) => ({ ...current, to: event.currentTarget.value }))
            }
            placeholder={t('mail.compose.toPlaceholder')}
          />
          <TextField
            label={t('mail.compose.cc')}
            value={compose.cc}
            onChange={(event) =>
              setCompose((current) => ({ ...current, cc: event.currentTarget.value }))
            }
          />
          <TextField
            label={t('mail.compose.subject')}
            value={compose.subject}
            onChange={(event) =>
              setCompose((current) => ({ ...current, subject: event.currentTarget.value }))
            }
          />
          <textarea
            className="mail-compose__text"
            rows={8}
            value={compose.text}
            onChange={(event) =>
              setCompose((current) => ({ ...current, text: event.currentTarget.value }))
            }
            placeholder={t('mail.compose.placeholder')}
          />
          <div className="mailbox-form__actions">
            <Button
              variant="primary"
              disabled={
                busy ||
                compose.mailboxId === '' ||
                compose.to.trim() === '' ||
                compose.text.trim() === ''
              }
              onClick={() => void sendCompose()}
            >
              {t('mail.compose.send')}
            </Button>
            <Button disabled={busy} onClick={() => setComposing(false)}>
              {t('mail.compose.cancel')}
            </Button>
          </div>
        </section>
      )}

      {mailboxes !== null && mailboxes.length > 1 && (
        <nav className="mailbox-chips" aria-label={t('today.scope.label')}>
          <button
            type="button"
            className={
              scopeMailboxId === null ? 'mailbox-chip mailbox-chip--active' : 'mailbox-chip'
            }
            aria-pressed={scopeMailboxId === null}
            onClick={() => setScopeMailboxId(null)}
          >
            {t('today.scope.all')}
          </button>
          {mailboxes.map((mailbox) => (
            <button
              key={mailbox.id}
              type="button"
              className={
                scopeMailboxId === mailbox.id ? 'mailbox-chip mailbox-chip--active' : 'mailbox-chip'
              }
              aria-pressed={scopeMailboxId === mailbox.id}
              onClick={() => setScopeMailboxId(mailbox.id)}
            >
              {mailbox.emailAddress}
            </button>
          ))}
        </nav>
      )}

      {threads.threads.length === 0 ? (
        <section className="card">
          <p className="empty__body">{t('mail.empty')}</p>
        </section>
      ) : (
        <ul className="thread-list">
          {threads.threads.map((thread) => (
            <li key={thread.threadId}>
              <button
                type="button"
                className={thread.unreadCount > 0 ? 'thread thread--unread' : 'thread'}
                onClick={() => void openConversation(thread)}
              >
                <span className="thread__from">{thread.lastFromName ?? thread.lastFrom}</span>
                <span className="thread__subject">
                  {thread.subject === '' ? t('mail.noSubject') : thread.subject}
                  {thread.messageCount > 1 && (
                    <span className="thread__count">{thread.messageCount}</span>
                  )}
                </span>
                <span className="thread__snippet">{thread.snippet}</span>
                <span className="thread__meta">
                  {translator.formatDate(new Date(thread.lastAt))}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
